import * as PleasedApi from '@/common/api/api.pleased';
import { SystemOptionType } from '../enum';
import { EventType } from '../lib';
import { noop, metaFilter, isPromise } from './ChatFramework.utils';
import { ChatFrameworkInterceptor } from './ChatFramework.types';

async function checkTrigger ({
  type,
  id,
  userCountry,
}: {
  type: 'CATEGORY' | 'ARTICLE' | 'SECTION';
  id: number | string;
  userCountry: string;
}, onMatch: TriggerMatchListener): Promise<void | MatchedChatTrigger> {
  return PleasedApi.matchOverlayTrigger
    .cached({ type, id: +id, userCountry })
    .then((matchedTrigger) => {
      if (!matchedTrigger?.message?.length) return;
      const { chatTriggerId } = matchedTrigger;

      let resolveTrigger: (value: 'END_CHAT'|'CONTINUE') => void;
      const triggerResolver = new Promise<'END_CHAT'|'CONTINUE'>(resolve => { resolveTrigger = resolve; });

      const trigger = {
        ...matchedTrigger,
        report: {
          end: () => PleasedApi.trackOverlyClick({ type: 'END_CHAT', chatTriggerId }).catch(noop).then(() => resolveTrigger('END_CHAT')),
          continue: () => PleasedApi.trackOverlyClick({ type: 'CONTINUE', chatTriggerId }).catch(noop).then(() => resolveTrigger('CONTINUE')),
        },
        waitForResolution: () => triggerResolver,
      };

      onMatch(trigger);

      return trigger;
    })
    .catch(noop);
}
interface TriggerInterceptorOptions {
  userCountry: string;
  onMatch: TriggerMatchListener;
}

/**
 * Create an interceptor for chat messages that can check for triggers and
 * execute triggers.
 * NOTE: This only checks for article and section, this does not check for
 * article triggers as the triggers are checked on article page. This is due to a
 * limitation, in V2 chat framework, when clicked on an article, there is no article message
 * show, so there is no message to intercept.
 *
 * In future versions, we can tackle this via ChatFramework action Interceptors
 */
export function createTriggerInterceptor ({ userCountry, onMatch }: TriggerInterceptorOptions) {
  const types = {
    [SystemOptionType.ARTICLE]: 'ARTICLE',
    [SystemOptionType.CATEGORY]: 'CATEGORY',
    [SystemOptionType.SECTION]: 'SECTION',
  } as const;

  const handler: ChatFrameworkInterceptor = {
    intercept (item, metadata) {
      const evt = metadata?.event;
      const tagsMatched = metaFilter(metadata?.tags).all('from:system', 'fetching').any('opt.section', 'opt.category').get();
      if (!EventType.isOptionEvent(evt) || !isPromise(item) || !tagsMatched) return item;
      // When option was selected, and we are fetching the data for that option.
      const { payload: { selected: option } } = evt;

      const futureMessage = item;

      return Promise.all([futureMessage, checkTrigger({ type: types[option.kind], id: option.id, userCountry }, onMatch)])
        .then(([result, trigger]) => {
          if (!trigger) return result;
          // Wait for resolution, only then allow the article messages to be added to the queue.
          return trigger.waitForResolution().then(() => result);
        });
    },
  };

  return handler;
}

export const manualTriggerCheck = checkTrigger;

export interface MatchedChatTrigger {
  report: {
    end: () => Promise<void>;
    continue: () => Promise<void>;
  };
  message: string;
  chatTriggerId: number;
  waitForResolution(): Promise<'END_CHAT'|'CONTINUE'>;
}

interface TriggerMatchListener {
  (match: MatchedChatTrigger): void;
}
